
import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Phone, User, Building, AlertCircle } from 'lucide-react';
import { LeadContact } from '@/services/leadContactsService';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface CallConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirmCall: () => void;
  contact: LeadContact | null;
  isInitiating: boolean;
  micPermission: 'granted' | 'denied' | 'prompt';
}

export const CallConfirmationDialog = ({
  isOpen,
  onClose,
  onConfirmCall,
  contact,
  isInitiating,
  micPermission
}: CallConfirmationDialogProps) => {
  if (!contact) return null;

  const phoneNumber = contact.phone_number || contact.phone;
  const fullName = `${contact.first_name || ''} ${contact.last_name || ''}`.trim();
  const companyName = contact.company?.name;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5" />
            Confirm Call
          </DialogTitle>
          <DialogDescription>
            You are about to call this contact using Twilio Voice.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Contact Information */}
          <div className="rounded-lg border p-4 space-y-3">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{fullName}</span>
            </div>
            
            {companyName && (
              <div className="flex items-center gap-2">
                <Building className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">{companyName}</span>
              </div>
            )}
            
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-muted-foreground" />
              <span className="font-mono text-sm">
                {typeof contact.phone === 'string' ? contact.phone : 
                  Array.isArray(contact.phone) && contact.phone.length > 0 ? 
                    contact.phone.find(p => p.is_primary)?.phone || contact.phone[0]?.phone || 'No phone' : 
                    'No phone'}
              </span>
            </div>
          </div>

          {/* Microphone Permission Warning */}
          {micPermission === 'denied' && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Microphone access is required for calls. Please enable microphone permissions in your browser.
              </AlertDescription>
            </Alert>
          )}

          {/* Call Preparation Status */}
          {isInitiating && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Preparing call... Please wait while we connect you.
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isInitiating}>
            Cancel
          </Button>
          <Button 
            onClick={onConfirmCall} 
            disabled={micPermission === 'denied' || isInitiating}
            className="flex items-center gap-2"
          >
            <Phone className="h-4 w-4" />
            {isInitiating ? 'Connecting...' : 'Call Now'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
